import { Component, type ErrorInfo, type ReactNode } from "react";
import { t } from "../i18n";
import { isStaleChunk } from "../services/staleChunk";

interface Props {
  children: ReactNode;
  /** "app" renders a full-page fallback; "section" an inline card so the rest of the dashboard keeps working. */
  scope?: "app" | "section";
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("UI error", error, info.componentStack);
    const boot = (window as any).__ADR_BOOT;
    if (boot?.errors) boot.errors.push(String(error?.message || error));
  }

  componentDidUpdate(prev: Props) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.scope === "section") {
      return (
        <div role="alert" className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          <p className="font-semibold">{t("This section failed to render.")}</p>
          <p className="mt-1 break-words font-mono text-xs">{error.message}</p>
          {/* A page file from an older build cannot be retried in place; only a reload fetches the new one. */}
          <button className="btn-secondary mt-3" onClick={() => (isStaleChunk(error) ? window.location.reload() : this.setState({ error: null }))}>
            {t("Try again")}
          </button>
        </div>
      );
    }
    return (
      <div role="alert" className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <h1 className="text-lg font-semibold">{t("Something went wrong")}</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            {t("The dashboard hit an unexpected error. Your recovery jobs keep running on the server.")}
          </p>
          <pre className="mt-3 max-h-40 overflow-auto rounded bg-slate-100 p-2 text-xs dark:bg-slate-800">{error.message}</pre>
          <div className="mt-4 flex gap-2">
            <button className="btn-primary" onClick={() => window.location.reload()}>
              {t("Reload")}
            </button>
            <button className="btn-secondary" onClick={() => this.setState({ error: null })}>
              {t("Try to continue")}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
